import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DataTable from '../../components/common/DataTable';
import StatusBadge from '../../components/common/StatusBadge';
import { usePagedData } from '../../hooks/usePagedData';
import { productsApi } from '../../api/endpoints';
import { ProductList, ProductStatusMap, ChannelMap } from '../../types';
import Select from '../../components/common/Select';
import { FiRefreshCw, FiSearch, FiDownload, FiX, FiDollarSign } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { exportApi } from '../../api/endpoints';
import { downloadExcel } from '../../utils/downloadFile';
import { SkeletonTable } from '../../components/common/Skeleton';

const ProductsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [brandInput, setBrandInput] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);

  const {
    data,
    loading,
    query,
    filters,
    setFilters,
    setPage,
    setPageSize,
    setSort,
    setSearch,
    refresh,
  } = usePagedData<ProductList>(productsApi.list, {
    initialSortBy: 'title',
    initialSortDesc: false,
  });

  const handleSearchSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setSearch(searchInput);
    },
    [searchInput, setSearch]
  );

  const handleStatusFilter = useCallback(
    (value: string) => {
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        status: value === '' ? undefined : Number(value),
      }));
    },
    [setFilters]
  );

  const handleChannelFilter = useCallback(
    (value: string) => {
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        channel: value === '' ? undefined : Number(value),
      }));
    },
    [setFilters]
  );

  const handleBrandFilter = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        brand: brandInput || undefined,
      }));
    },
    [brandInput, setFilters]
  );

  const handleSync = useCallback(async () => {
    setSyncing(true);
    try {
      await productsApi.sync();
      toast.success(t('products.syncStarted'));
      refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('products.syncError'));
    } finally {
      setSyncing(false);
    }
  }, [refresh]);

  const handleBulkSyncPricing = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setBulkProcessing(true);
    try {
      const result = await productsApi.bulkSyncPricing(Array.from(selectedIds));
      const r = result.data;
      toast.success(t('products.bulkSyncSuccess', { success: r.success, total: r.total }));
      setSelectedIds(new Set());
      refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('products.bulkSyncError'));
    } finally {
      setBulkProcessing(false);
    }
  }, [selectedIds, refresh]);

  const handleRowClick = useCallback(
    (row: ProductList) => {
      navigate(`/products/${row.id}`);
    },
    [navigate]
  );

  const formatCurrency = (amount: number | null) =>
    amount !== null
      ? amount.toLocaleString('pl-PL', { style: 'currency', currency: 'EUR' })
      : '-';

  const columns = [
    {
      key: 'asin',
      header: 'ASIN',
      sortable: true,
    },
    {
      key: 'sku',
      header: 'SKU',
      sortable: true,
      render: (row: ProductList) => row.sku || '-',
    },
    {
      key: 'title',
      header: t('products.title_column'),
      sortable: true,
      render: (row: ProductList) => (
        <span className="text-truncate" title={row.title}>
          {row.title.length > 60 ? row.title.substring(0, 60) + '...' : row.title}
        </span>
      ),
    },
    {
      key: 'brand',
      header: t('products.brand'),
      sortable: true,
      render: (row: ProductList) => row.brand || '-',
    },
    {
      key: 'listPrice',
      header: t('products.price'),
      sortable: true,
      render: (row: ProductList) => formatCurrency(row.listPrice),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (row: ProductList) => (
        <StatusBadge type="product" value={row.status} />
      ),
    },
    {
      key: 'channel',
      header: t('products.channel'),
      render: (row: ProductList) => (
        <StatusBadge type="channel" value={row.channel} />
      ),
    },
    {
      key: 'availableQuantity',
      header: t('products.available'),
      sortable: true,
      render: (row: ProductList) => row.availableQuantity,
    },
  ];

  return (
    <div className="page-container page-container--wide">
      <div className="page-header">
        <h1>{t('products.title')}</h1>
        <div className="page-actions">
          <button
            className="btn btn-secondary"
            onClick={() => downloadExcel(exportApi.productsXlsx, `produkty_${new Date().toISOString().slice(0,10)}.xlsx`)}
          >
            <FiDownload /> Excel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSync}
            disabled={syncing}
          >
            <FiRefreshCw className={syncing ? 'spin' : ''} />
            {syncing ? t('products.syncing') : t('products.syncWithAmazon')}
          </button>
        </div>
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="bulk-actions-bar">
          <span style={{ fontWeight: 600 }}>{t('common.selected')}: {selectedIds.size}</span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-primary btn-sm" onClick={handleBulkSyncPricing} disabled={bulkProcessing}>
              <FiDollarSign /> {bulkProcessing ? t('products.bulkSyncing') : t('products.bulkSyncPricing')}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => setSelectedIds(new Set())} disabled={bulkProcessing}>
              <FiX /> {t('products.deselect')}
            </button>
          </div>
        </div>
      )}

      <div className="toolbar">
        <form className="toolbar-search" onSubmit={handleSearchSubmit}>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder={t('products.searchPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button type="submit" className="btn btn-secondary">
              <FiSearch />
            </button>
          </div>
        </form>

        <div className="toolbar-filters">
          <Select
            options={[
              { value: '', label: t('common.allStatuses') },
              ...Object.entries(ProductStatusMap).map(([key, label]) => ({ value: key, label })),
            ]}
            value={String(filters.status ?? '')}
            onChange={handleStatusFilter}
            placeholder={t('common.allStatuses')}
          />

          <Select
            options={[
              { value: '', label: t('products.allChannels') },
              ...Object.entries(ChannelMap).map(([key, label]) => ({ value: key, label })),
            ]}
            value={String(filters.channel ?? '')}
            onChange={handleChannelFilter}
            placeholder={t('products.allChannels')}
          />

          <form onSubmit={handleBrandFilter} style={{ display: 'flex', gap: '0.25rem' }}>
            <input
              type="text"
              className="form-control"
              placeholder={t('products.filterBrand')}
              value={brandInput}
              onChange={(e) => setBrandInput(e.target.value)}
            />
            <button type="submit" className="btn btn-secondary btn-sm">
              <FiSearch />
            </button>
          </form>
        </div>
      </div>

      {loading ? (
        <SkeletonTable rows={10} cols={8} />
      ) : (
        <DataTable
          columns={columns}
          data={data?.items || []}
          totalCount={data?.totalCount || 0}
          page={query.page}
          pageSize={query.pageSize}
          totalPages={data?.totalPages || 0}
          sortBy={query.sortBy}
          sortDescending={query.sortDescending}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onSort={setSort}
          onRowClick={handleRowClick}
          selectable
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
      )}
    </div>
  );
};

export default ProductsPage;
